import { realtime } from '../api/realtime.js';
import { matchClient } from '../api/matchClient.js';
import { SCENE } from '../config.js';
import { gameState } from '../state/gameState.js';
import { virtualInput } from './virtualInput.js';
import { isInputLocked } from './inputLock.js';
import { showActionsModal } from './Modal.js';
import { showToast } from './Toast.js';
import { ySortDepth } from './ySort.js';
import { ChatBubble } from './ChatBubble.js';
import { attachRoomChat } from './ChatInput.js';
import { audioManager } from '../audio/AudioManager.js';
import { NATALKA_SIZE } from './NatalkaSprite.js';
import { BOARD_OVERRIDES } from '../scenes/gameHallLayout.js';
import { nameWithIcon } from './botLabel.mjs';
import { iconText } from './iconText.js';

const SIT_RADIUS = 55;        // feet distance to a seat anchor that offers "Usiądź"
const CLICK_SIT_RANGE = 170;  // feet distance within which clicking/tapping a chair sits you
const SIT_LABEL = 'Usiądź {chair}';

const ERROR_MSGS = {
  SEAT_TAKEN: 'To krzesło jest zajęte',
  NOT_HOST: 'Tylko gospodarz stołu wybiera grę',
  GAME_NOT_OWNED: 'Nie masz tej gry — kup ją przy ladzie',
  GAME_NOT_AVAILABLE: 'Ta gra będzie dostępna wkrótce',
  UNKNOWN_SEAT: 'Nie można tu usiąść',
  NOT_SEATED: 'Najpierw usiądź przy stole',
  // --- Game Hall match verbs (Plan 3, Task 5) ---
  NOT_ENOUGH_PLAYERS: 'Za mało graczy przy stole',
  NO_GAME_SELECTED: 'Najpierw połóż grę na stole',
  // Covers all three server uses of this code: table already has a match, you have a
  // match elsewhere, and (mid-match stand/sit guard) you tried to leave a running match.
  MATCH_IN_PROGRESS: 'Masz już rozgrywaną partię — dokończ ją najpierw',
  TABLE_FULL: 'Brak wolnego miejsca przy stole',
  NO_BOT_AT_TABLE: 'Przy stole nie ma bota',
};

function tableRoom(tableId) { return `game_hall:table:${tableId}`; }
function stripPalette(text) { return text.startsWith('palette:') ? text.slice('palette:'.length) : text; }

// Renders table state (tabletop game sprites + seat nameplates) for one hall floor and
// drives sit/stand/host interactions. One instance per scene; destroys itself on shutdown.
export class GameHallTables {
  constructor(scene, { tables, presence, floorRoom }) {
    this.scene = scene;
    this.tables = tables;                 // DOWN_TABLES or UP_TABLES
    this.presence = presence;             // attachRemotePresence handle (avatars map)
    this.floorRoom = floorRoom;           // 'game_hall:down' / 'game_hall:up' — this floor's room
    this.state = new Map();               // tableId -> last table_state payload
    this.mySeat = null;                   // {tableId, seat} when seated
    this._sitTargetKey = null;            // `${tableId}:${seat}` currently offered by the sit action
    this._sprites = new Map();            // tableId -> Phaser image (tabletop game)
    this._plates = new Map();             // `${tableId}:${seat}` -> text label
    this._bubbles = new Map();            // `${tableId}:${seat}` -> ChatBubble (table chat)
    this._tableChat = null;               // free-text chat handle for my table room
    this._toMatch = false;                // set when leaving FOR the board scene (skip stand)
    this._unsubs = [
      realtime.on('table_state', (msg) => this._onTableState(msg)),
      realtime.on('table_error', (msg) => this._onTableError(msg)),
      realtime.on('chat', (msg) => this._onChat(msg)),
      realtime.on('roster', (msg) => this._onRoster(msg)),
      // A match starting at (or resuming for) my table jumps me into the board scene.
      // These only reach me when I'm a player or a seated spectator of that table.
      realtime.on('match_started', (msg) => this._onMatchStarted(msg)),
      realtime.on('match_resume', (msg) => this._onMatchResume(msg)),
      // Floor toast (Task 5): a match finishing ANYWHERE on this floor. Never reaches
      // us for a match we were just in — its players/seated spectators are away in
      // BoardGameScene (this GameHallTables instance doesn't exist yet) for the whole
      // exchange, so there is no separate filter to apply here.
      realtime.on('match_over', (msg) => this._onMatchOver(msg)),
    ];
    // Clickable chairs: an invisible hit zone per seat shows the hand cursor and sits
    // you on click/tap (occupied seats get disableInteractive'd in _render).
    this._seatZones = new Map();            // `${tableId}:${seatId}` -> Phaser zone
    for (const def of this.tables) {
      for (const seat of def.seats) {
        const zone = scene.add.zone(seat.x, seat.y - 18, 64, 76)
          .setInteractive({ useHandCursor: true });
        zone.on('pointerup', () => this._onSeatClick(def, seat));
        this._seatZones.set(`${def.id}:${seat.id}`, zone);
      }
    }
    scene.events.on('shutdown', () => this.destroy());
  }

  // A fresh roster for this floor means we (re)subscribed — most importantly on a
  // WS reconnect. If the socket dropped while we were seated the server auto-stood
  // us, but the floor re-subscribe only re-sends table_state for NON-EMPTY tables,
  // so a solo sitter would get nothing and stay frozen (walker off, avatar hidden,
  // stale mySeat). Reset all local seat/render state here; the table_state messages
  // that follow the roster repopulate occupied tables (and the fast-reconnect
  // re-seat case converges the same way).
  _onRoster(msg) {
    if (msg.room !== this.floorRoom) return;
    this.mySeat = null;
    this._sitTargetKey = null;
    this._setSeatedLocal(false);          // re-enable walker, drop table chat/bubbles
    this.state.clear();
    this._sprites.forEach((s) => s.destroy()); this._sprites.clear();
    this._plates.forEach((p) => p.destroy()); this._plates.clear();
    this._bubbles.forEach((b) => b?.destroy?.()); this._bubbles.clear();
    for (const z of this._seatZones.values()) z.setInteractive({ useHandCursor: true });
    // No table_state follows for a now-empty table, so _render won't run — make the
    // local walking avatar visible again explicitly (this is the un-freeze).
    this.scene.natalka.setVisible(true);
    // Avatar-flash fix (Plan 3, Task 5): the roster's `seated` list carries pids
    // already seated at this floor's tables. Hide their remote avatars right now
    // instead of waiting for the table_state seeds that follow (one broadcast later,
    // and only for non-empty tables) — presence's own roster handler is registered
    // BEFORE this one (attachRemotePresence runs first in GameHallScene.create), so
    // the avatars already exist here.
    if (this.presence && msg.seated) {
      for (const pid of msg.seated) this.presence.avatars.get(pid)?.setHidden(true);
    }
  }

  _tableDef(id) { return this.tables.find((t) => t.id === id); }

  // Click/tap on a chair: sit if it's free and we're close enough. At our own table a
  // click on another free chair is a lateral move (server preserves host + sit order).
  _onSeatClick(def, seat) {
    if (isInputLocked(this.scene)) return;
    const table = this.state.get(def.id);
    if (table?.seats?.[seat.id]) return;    // taken (zone disable lags a broadcast at worst)
    if (this.mySeat) {
      if (this.mySeat.tableId !== def.id) { showToast(this.scene, 'Najpierw wstań od stołu'); return; }
      realtime.tableSit(def.id, seat.id);
      return;
    }
    const fx = this.scene.natalka.x;
    const fy = this.scene.natalka.feetY ?? this.scene.natalka.y + NATALKA_SIZE.h / 2;
    if (Math.hypot(fx - seat.x, fy - seat.y) > CLICK_SIT_RANGE) {
      showToast(this.scene, 'Podejdź bliżej krzesła {chair}');
      return;
    }
    realtime.tableSit(def.id, seat.id);
  }

  _onTableState(msg) {
    const def = this._tableDef(msg.table.id);
    if (!def) return;                     // other floor
    const prevTable = this.state.get(msg.table.id);
    this.state.set(msg.table.id, msg.table);
    const myId = window.__myId;
    const mine = Object.entries(msg.table.seats).find(([, s]) => s.id === myId);
    const justSat = !!mine && this.mySeat?.tableId !== msg.table.id;
    if (mine) this.mySeat = { tableId: msg.table.id, seat: mine[0] };
    else if (this.mySeat?.tableId === msg.table.id) { this.mySeat = null; this._setSeatedLocal(false); }
    if (mine) this._setSeatedLocal(true);
    if (justSat) audioManager.playSfx('sit_chair');
    if (mine && msg.table.game && msg.table.game !== prevTable?.game) audioManager.playSfx('place_board_game');
    this._render(def, msg.table);
  }

  _onTableError(msg) {
    showToast(this.scene, ERROR_MSGS[msg.error_code] ?? 'Nie można wykonać tej akcji');
  }

  // Floor toast (Task 5): someone's match finished somewhere on this floor. Never
  // fires for a match we were just in — see the registration note above.
  _onMatchOver(msg) {
    if (msg.result === 'void') return;   // nothing worth announcing
    const catalog = gameState.raw?.catalog?.board_games ?? {};
    const gameName = catalog[msg.game]?.name_pl ?? msg.game ?? 'grze';
    if (msg.result === 'draw') {
      const [n1, n2] = msg.player_names ?? [];
      if (n1 && n2) showToast(this.scene, `Remis między ${n1} i ${n2}`);
      return;
    }
    if (msg.winner_name && msg.loser_name) {
      showToast(this.scene, `${nameWithIcon(msg.winner_name)} wygrał(a) z ${nameWithIcon(msg.loser_name)} w ${gameName}! {trophy}`);
    }
  }

  // A fresh match at my table: attach the client (buffers the match_state that follows on
  // the wire) then jump to the board scene. Reaches me only as a player or seated spectator.
  _onMatchStarted(msg) {
    const myColor = this._myColorIn(msg.players);
    matchClient.attach({
      matchId: msg.match_id, game: msg.game, tableId: msg.table_id,
      players: msg.players, myColor,
    });
    this._enterMatch(msg.table_id);
  }

  // Reconnect: fired after we re-subscribe to the floor when we have an active match.
  // match_resume carries the authoritative players map (with is_bot flags) from the
  // server; fall back to a best-effort rebuild from table_state (host = 'w') only
  // against an old server that doesn't send it. The following match_state resolves
  // my_color authoritatively regardless.
  _onMatchResume(msg) {
    const players = msg.players ?? this._playersFromTable(msg.table_id);
    matchClient.attach({
      matchId: msg.match_id, game: msg.game, tableId: msg.table_id,
      players, myColor: this._myColorIn(players),
    });
    this._enterMatch(msg.table_id);
  }

  _myColorIn(players) {
    const id = window.__myId;
    if (players?.w?.id === id) return 'w';
    if (players?.b?.id === id) return 'b';
    return null;   // spectator
  }

  // Best-effort {w,b} from a table_state (host is 'w'). Only when exactly two seats are
  // occupied, to avoid mislabeling when a spectator also sits at the table.
  _playersFromTable(tableId) {
    const t = this.state.get(tableId);
    if (!t?.seats) return {};
    const occ = Object.values(t.seats);
    if (occ.length !== 2) return {};
    const host = occ.find((o) => o.id === t.host);
    const other = occ.find((o) => o.id !== t.host);
    const players = {};
    if (host) players.w = { id: host.id, name: host.name, is_bot: !!host.is_bot };
    if (other) players.b = { id: other.id, name: other.name, is_bot: !!other.is_bot };
    return players;
  }

  _enterMatch(tableId) {
    if (this._toMatch) return;   // guard against a duplicate started/resume
    this._toMatch = true;
    // Clear ANY of our own registered actions (same label set as update()'s guards),
    // not just the sit offer: a seated player enters the match with "Stół 🎲"/"Wstań"
    // registered, and BoardGameScene never re-registers — on mobile the stale button
    // stayed floating over the board, invoking a closure into this (destroyed) instance.
    const cur = virtualInput.getAction();
    if (cur && (cur.label === SIT_LABEL || cur.label === 'Stół {dice}' || cur.label === 'Wstań')) {
      virtualInput.clearAction();
    }
    this.scene.scene.start(SCENE.BOARD_GAME, { ...matchClient.info, tableId, state: matchClient.state });
  }

  // Table free-chat: while seated I subscribe to my table's room. Show incoming messages
  // as a bubble above the sender's seat nameplate (their walking avatar is hidden, so a
  // bubble over the stale avatar position would float in the wrong place). My own echoed
  // message is shown by attachRoomChat's sayLocal (above my hidden-but-positioned avatar),
  // so skip it here to avoid a double bubble.
  _onChat(msg) {
    if (!this.mySeat) return;
    if (msg.room !== tableRoom(this.mySeat.tableId)) return;
    if (msg.peerId === window.__myId) return;
    const table = this.state.get(this.mySeat.tableId);
    const def = this._tableDef(this.mySeat.tableId);
    if (!table || !def) return;
    const entry = Object.entries(table.seats).find(([, s]) => s.id === msg.peerId);
    if (!entry) return;
    const seatDef = def.seats.find((s) => s.id === entry[0]);
    if (!seatDef) return;
    audioManager.playSfx('message_in');
    this._showSeatBubble(this.mySeat.tableId, entry[0], seatDef, stripPalette(msg.text));
  }

  _showSeatBubble(tableId, seatId, seatDef, text) {
    const k = `${tableId}:${seatId}`;
    this._bubbles.get(k)?.destroy?.();
    // ChatBubble only reads target.x / target.y — a static point above the chair works.
    this._bubbles.set(k, new ChatBubble(this.scene, { x: seatDef.x, y: seatDef.y - 40 }, text));
  }

  _render(def, table) {
    // Tabletop game sprite
    const key = `game_${table.game}`;
    let spr = this._sprites.get(def.id);
    if (table.game && this.scene.textures.exists(key)) {
      if (!spr) { spr = this.scene.add.image(def.top.x, def.top.y, key); this._sprites.set(def.id, spr); }
      spr.setTexture(key);
      // Contain-fit into the felt's drawn extent so the board never overflows the
      // tabletop; depth just above the table's own foreground cutout (which would
      // otherwise re-draw the background over the sprite = "cropped" board).
      // Placement precedence: per-table+game override → table's default board box →
      // felt extent (all authorable in the DEV edit mode).
      const box = BOARD_OVERRIDES[def.id]?.[table.game] ?? def.board ?? def.top;
      spr.setPosition(box.x ?? def.top.x, box.y ?? def.top.y);
      spr.setScale(Math.min((box.w ?? def.top.w) / spr.width, (box.h ?? def.top.h) / spr.height));
      spr.setAngle(box.angle ?? 0);
      spr.setDepth(def.top.depth);
      spr.setVisible(true);
    } else if (spr) { spr.setVisible(false); }

    // Seat nameplates
    for (const seatDef of def.seats) {
      const k = `${def.id}:${seatDef.id}`;
      const occupant = table.seats[seatDef.id];
      let plate = this._plates.get(k);
      if (occupant) {
        const isHost = occupant.id === table.host;
        const txt = `${isHost ? '{xp_star} ' : ''}${nameWithIcon(occupant.name)}`;   // bots: word name + face icon
        if (!plate) {
          plate = iconText(this.scene, seatDef.x, seatDef.y - 52, txt, {
            fontFamily: 'system-ui', fontSize: '15px', color: '#fff',
            backgroundColor: '#000000aa', padding: { x: 6, y: 2 },
          }).setOrigin(0.5).setDepth(900);
          this._plates.set(k, plate);
        } else plate.setText(txt);
      } else if (plate) {
        plate.destroy(); this._plates.delete(k);
        this._bubbles.get(k)?.destroy?.(); this._bubbles.delete(k);
      }
      // Hand cursor only on free chairs.
      const zone = this._seatZones.get(k);
      if (zone) {
        if (occupant) zone.disableInteractive();
        else zone.setInteractive({ useHandCursor: true });
      }
    }
    // Hide/show remote walking avatars of everyone seated at this floor's tables
    if (this.presence) {
      for (const [id, av] of this.presence.avatars) av.setHidden(this._isSeatedAnywhere(id));
    }
    // Local avatar visibility
    this.scene.natalka.setVisible(!this.mySeat);
  }

  // Re-apply the last known table_state to all visuals (used by the DEV edit mode after
  // it mutates layout geometry live, and to restore its board previews on exit).
  redraw() {
    for (const def of this.tables) {
      // Seat zones + nameplates track the (possibly edited) anchors.
      for (const seat of def.seats) {
        this._seatZones.get(`${def.id}:${seat.id}`)?.setPosition(seat.x, seat.y - 18);
        this._plates.get(`${def.id}:${seat.id}`)?.setPosition(seat.x, seat.y - 52);
      }
      const table = this.state.get(def.id);
      if (table) this._render(def, table);
      else this._sprites.get(def.id)?.setVisible(false);
    }
  }

  _isSeatedAnywhere(pid) {
    for (const t of this.state.values()) {
      if (Object.values(t.seats).some((s) => s.id === pid)) return true;
    }
    return false;
  }

  _setSeatedLocal(seated) {
    this.scene.walker.enabled = !seated;   // WalkController early-returns when disabled
    if (seated) {
      if (!this._tableChat && this.mySeat) {
        this.scene._roomChat?.setHidden?.(true);   // hide the floor palette while seated
        this._tableChat = attachRoomChat(this.scene, tableRoom(this.mySeat.tableId));
      }
    } else {
      this._tableChat?.detach?.();
      this._tableChat = null;
      this.scene._roomChat?.setHidden?.(false);
      for (const b of this._bubbles.values()) b?.destroy?.();
      this._bubbles.clear();
    }
  }

  _nearestFreeSeat(fx, fy) {
    let best = null, bestD = SIT_RADIUS;
    for (const def of this.tables) {
      const table = this.state.get(def.id);
      for (const seat of def.seats) {
        if (table?.seats?.[seat.id]) continue;   // taken
        const d = Math.hypot(fx - seat.x, fy - seat.y);
        if (d < bestD) { best = { tableId: def.id, seat: seat.id }; bestD = d; }
      }
    }
    return best;
  }

  update(fx, fy, { suppressSit = false } = {}) {
    // Lock-first idiom (mirrors GameHallScene's counter block): while a modal is open,
    // clear our own action so the mobile button doesn't sit over the modal, then bail —
    // never touch proximity register/clear while locked.
    if (isInputLocked(this.scene)) {
      const a = virtualInput.getAction();
      if (a && (a.label === SIT_LABEL || a.label === 'Stół {dice}' || a.label === 'Wstań')) virtualInput.clearAction();
      return;
    }
    const cur = virtualInput.getAction();
    if (this.mySeat) {
      // Seated: action = host game menu or stand
      const table = this.state.get(this.mySeat.tableId);
      const isHost = table?.host === window.__myId;
      const label = isHost ? 'Stół {dice}' : 'Wstań';
      if (cur?.label !== label) {
        // Look up table state fresh at invocation time rather than closing over `table` —
        // the closure is registered once per seat/label and would otherwise keep pointing
        // at the object captured at sit-time, missing later updates (e.g. placing a game)
        // for as long as the label stays "Stół 🎲" (host never re-registers just by playing).
        virtualInput.registerAction(label, () => {
          // Defensive: if we stood up between registering and invoking (see the
          // un-seated branch below, which should normally clear this first), bail
          // instead of dereferencing a now-null mySeat.
          if (!this.mySeat) return;
          return isHost ? this._hostMenu(this.state.get(this.mySeat.tableId)) : realtime.tableStand();
        });
      }
      return;
    }
    // suppressSit: the caller (GameHallScene counter block) owns the action here —
    // the counter zone overlaps t1's seat radius, so let "Kup gry 🎲" win instead of
    // re-registering "Usiądź" against it every frame. Treat as no seat nearby, which
    // also clears any sit offer we left registered stepping into the zone.
    const near = suppressSit ? null : this._nearestFreeSeat(fx, fy);
    if (near) {
      // Re-register when either the offer appears OR the nearest free seat changes, so the
      // captured target never goes stale (e.g. brushing past one free chair to another).
      const targetKey = `${near.tableId}:${near.seat}`;
      if (cur?.label !== SIT_LABEL || this._sitTargetKey !== targetKey) {
        this._sitTargetKey = targetKey;
        virtualInput.registerAction(SIT_LABEL, () => realtime.tableSit(near.tableId, near.seat));
      }
    } else if (cur && (cur.label === SIT_LABEL || cur.label === 'Stół {dice}' || cur.label === 'Wstań')) {
      // Clear ANY of our own stale actions here, not just the sit offer — standing up
      // (mySeat: seat → null) with no seat nearby must not leave "Stół 🎲"/"Wstań" stuck
      // registered from before we stood (same label set as the lock-first guard above).
      this._sitTargetKey = null;
      virtualInput.clearAction();
    }
  }

  async _hostMenu(table) {
    const owned = gameState.raw?.inventory?.board_games ?? [];
    const catalog = gameState.raw?.catalog?.board_games ?? {};
    const def = this._tableDef(table.id);
    const seated = Object.keys(table.seats).length;
    const freeSeat = seated < (def?.seats?.length ?? 4);
    const actions = [];
    if (table.game) {
      // A game is on the table: offer start (gated on player count) and taking it back.
      const spec = catalog[table.game];
      const minPlayers = spec?.min_players ?? 2;
      if (seated >= minPlayers) {
        actions.push({ label: 'Rozpocznij grę {arrow_right}', fn: () => realtime.tableStart() });
      } else {
        actions.push({ label: `Rozpocznij grę (potrzeba ${minPlayers} graczy)`, disabled: true });
      }
      actions.push({ label: 'Zabierz grę ze stołu', fn: () => realtime.tableClearGame() });
    } else if (owned.length) {
      actions.push(...owned.map((id) => ({
        label: `Połóż: ${catalog[id]?.name_pl ?? id}`, fn: () => realtime.tableSelectGame(id),
      })));
    }
    // A bot fills a free seat regardless of whether the game is already selected or
    // still to be chosen (difficulty picker can wait — always difficulty 1 for now).
    const hasBot = Object.values(table.seats).some((s) => String(s.id).startsWith('bot:'));
    if (hasBot) {
      actions.push({ label: 'Zabierz bota {robot}', fn: () => realtime.tableRemoveBot() });
    } else if (freeSeat && (table.game || owned.length)) {
      // Not awaited: the host menu closes and the difficulty picker opens on top.
      actions.push({ label: 'Zagraj z botem {robot}', fn: () => { this._botDifficultyMenu(); } });
    }
    actions.push({ label: 'Wstań', fn: () => realtime.tableStand() });
    await showActionsModal(this.scene, { title: 'Twój stół', actions });
  }

  // Difficulty = the bot's minimax search depth (backend caps at 4).
  async _botDifficultyMenu() {
    await showActionsModal(this.scene, {
      title: 'Poziom bota {robot}',
      actions: [
        { label: 'Łatwy {face_smile}', fn: () => realtime.tableAddBot(1) },
        { label: 'Średni {face_think}', fn: () => realtime.tableAddBot(2) },
        { label: 'Trudny {face_wow}', fn: () => realtime.tableAddBot(3) },
        { label: 'Mistrz {brain}', fn: () => realtime.tableAddBot(4) },
      ],
    });
  }

  destroy() {
    this._unsubs.forEach((u) => u?.());
    this._sprites.forEach((s) => s.destroy());
    this._plates.forEach((p) => p.destroy());
    this._bubbles.forEach((b) => b?.destroy?.());
    this._seatZones.forEach((z) => z.destroy());
    this._seatZones.clear();
    this._tableChat?.detach?.();
    this._tableChat = null;
    // Any of our own labels — a 'Stół {dice}' / 'Wstań' left registered stayed on the street.
    if ([SIT_LABEL, 'Stół {dice}', 'Wstań'].includes(virtualInput.getAction()?.label)) virtualInput.clearAction();
    // Walking out of the scene stands you up — but NOT when we're transitioning to the
    // board scene (the server keeps the seat reserved for the whole match).
    if (this.mySeat && !this._toMatch) realtime.tableStand();
  }
}
